import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, CopyX, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/* Doppelte Check-ins (15.10.2026, backend/checkin_doppelt.py): Bis zur Sperre zählte die App einen Scan manchmal
   mehrfach (bis zu 14 Einträge in einem Augenblick). Die Leitung sieht hier, was doppelt ist, und bereinigt es mit
   einem Klick — der erste Eintrag jedes Besuchs bleibt, die übrigen werden aufbewahrt und aus der Zählung genommen.
   Nur Leitung (wie „Check-in nachtragen“); alle anderen sehen die Karte nicht.
   Kursbuchungen ändert die Bereinigung nicht: Wurde bei einem doppelten Scan zusätzlich ein weiterer Kurs des Tages
   als besucht vermerkt, steht das hier als „bitte prüfen“ (Vorschau und Ergebnis) — entscheiden muss die Leitung. */
const tag = (iso) => (iso ? iso.split('-').reverse().join('.') : '');
const mal = (n) => `${n}× gezählt`;
const mitvermerkt = (p) => `Dabei wurde auch „${p.kurs}“ als besucht vermerkt – bitte prüfen, ob die Person dort war`;

function Person({ p }) {
  const [offen, setOffen] = useState(false);
  const pruefen = p.gruppen.reduce((n, g) => n + (g.pruefen || []).length, 0);
  return (
    <li className="border-t border-border/50 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/leads/${p.member_id}`} className="font-medium text-sm underline-offset-2 hover:underline">{p.name}</Link>
        <span className="text-xs text-muted-foreground tabular-nums">
          {p.eintraege_jetzt} Einträge, richtig wären {p.eintraege_danach}
        </span>
        {pruefen > 0 && <span className="text-xs text-marke">bitte prüfen</span>}
        <button type="button" onClick={() => setOffen((o) => !o)}
                className="ml-auto text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground">
          {offen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {p.gruppen.length === 1 ? '1 Besuch' : `${p.gruppen.length} Besuche`}
        </button>
      </div>
      {offen && (
        <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
          {p.gruppen.map((g) => (
            <li key={g.behalten_id} className="tabular-nums">
              {tag(g.tag)} um {g.uhrzeit.slice(0, 5)} Uhr{g.kurs ? ` · ${g.kurs}` : ''} — {mal(g.anzahl)}, richtig ist 1
              {(g.pruefen || []).map((b) => (
                <span key={b.buchung_id} className="block text-marke">{mitvermerkt(b)}</span>
              ))}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function DoppelteCheckins() {
  const { user, userPermissions, isAdminPermissions } = useAuth();
  const leitung = ['admin', 'manager'].includes(user?.role) && (isAdminPermissions || !!(userPermissions || {})['leads.edit']);
  const [daten, setDaten] = useState(null);
  const [fragen, setFragen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);
  const [alleZeigen, setAlleZeigen] = useState(false);

  const laden = useCallback(() => {
    api.get('/checkins/doppelt').then((r) => setDaten(r.data)).catch(() => setDaten(false));
  }, []);
  useEffect(() => { if (leitung) laden(); }, [leitung, laden]);
  if (!leitung || daten === false) return null;
  // Nichts (mehr) doppelt: die Karte verschwindet — außer direkt nach dem Bereinigen, dann steht dort das Ergebnis
  if (daten && daten.summe.doppelt === 0 && !ergebnis) return null;

  const bereinigen = async () => {
    setLaeuft(true);
    try {
      const r = (await api.post('/checkins/doppelt/bereinigen', {})).data;
      setErgebnis(r);
      toast.success(r.entfernt ? `${r.entfernt} doppelte Check-ins entfernt` : 'Es war nichts mehr doppelt');
      setFragen(false);
      laden();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bereinigen hat nicht geklappt — bitte noch einmal versuchen');
    }
    setLaeuft(false);
  };

  const summe = daten?.summe;
  const personen = daten?.personen || [];
  const sichtbar = alleZeigen ? personen : personen.slice(0, 8);
  return (
    <Card className="border-border/60" data-testid="doppelte-checkins">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3 flex-wrap">
          <CopyX className="w-5 h-5 text-marke mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[14rem]">
            <p className="font-semibold text-sm">Doppelte Check-ins</p>
            <p className="text-xs text-muted-foreground">
              Bis zum 15.10.2026 hat die App einen Scan manchmal mehrfach gezählt. Der erste Eintrag jedes Besuchs
              bleibt, die übrigen werden entfernt — danach stimmen Trainingszahl und Gürtel-Fortschritt wieder.
              Punkte ändern sich nicht.
            </p>
          </div>
          {summe?.doppelt > 0 && (
            <Button size="sm" onClick={() => setFragen(true)} disabled={laeuft}>Bereinigen</Button>
          )}
        </div>

        {!daten ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird geprüft …</p>
        ) : summe.doppelt === 0 ? (
          <>
            <p className="text-sm text-muted-foreground">
              {ergebnis?.entfernt ? `Erledigt: ${ergebnis.entfernt} doppelte Check-ins entfernt. ` : ''}Keine doppelten Check-ins gefunden.
            </p>
            {(ergebnis?.pruefen || []).length > 0 && (
              <div className="text-xs space-y-0.5">
                <p className="font-medium">Bitte noch prüfen (die Bereinigung hat daran nichts geändert):</p>
                <ul className="text-muted-foreground tabular-nums">
                  {ergebnis.pruefen.map((b) => (
                    <li key={b.buchung_id}>
                      <Link to={`/leads/${b.member_id}`} className="underline-offset-2 hover:underline">{b.name}</Link>
                      {' '}am {tag(b.tag)}: „{b.kurs}“ als besucht vermerkt
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm">
              <span className="font-semibold tabular-nums text-marke">{summe.doppelt}</span> doppelte Check-ins bei{' '}
              <span className="font-semibold tabular-nums">{summe.personen}</span> {summe.personen === 1 ? 'Person' : 'Personen'}
            </p>
            {summe.pruefen > 0 && (
              <p className="text-xs text-muted-foreground">
                Bei {summe.pruefen === 1 ? 'einem Besuch' : `${summe.pruefen} Besuchen`} wurde durch den doppelten Scan
                zusätzlich ein weiterer Kurs als besucht vermerkt (unten mit „bitte prüfen“). Das ändert die Bereinigung
                nicht – bitte selbst nachsehen, ob die Person dort war.
              </p>
            )}
            <ul>{sichtbar.map((p) => <Person key={p.member_id} p={p} />)}</ul>
            {personen.length > sichtbar.length && (
              <button type="button" className="text-xs underline" onClick={() => setAlleZeigen(true)}>
                Alle {personen.length} anzeigen
              </button>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={fragen} onOpenChange={(o) => { if (!laeuft) setFragen(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Doppelte Check-ins bereinigen?</DialogTitle>
            <DialogDescription>
              {summe?.doppelt} doppelte Check-ins bei {summe?.personen} {summe?.personen === 1 ? 'Person' : 'Personen'} werden
              entfernt. Der erste Eintrag jedes Besuchs bleibt und übernimmt Kurs und Check-out der doppelten. Die
              entfernten Einträge werden gesichert aufbewahrt. Kursbuchungen bleiben, wie sie sind.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFragen(false)} disabled={laeuft}>Abbrechen</Button>
            <Button onClick={bereinigen} disabled={laeuft}>
              {laeuft && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Jetzt bereinigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
